/**
 * MonitoringCenterContext
 *
 * Consolidates all MonitoringCenter state to reduce prop drilling.
 * Instead of passing 88+ props through MonitoringCenter, components access
 * state and handlers via context hooks.
 *
 * State Categories:
 * - TabState: Tab navigation and switching
 * - SearchState: Search query and autocomplete
 * - AlertFilters: Severity, days, inventory range filters
 * - ProductFilters: Status, product type, vendor filters
 * - FilterActions: Clear/remove filter operations
 * - ActionsMenu: Actions menu state and handlers
 * - AlertsTabData: Active alerts data and actions
 * - ProductsTabData: Monitored products data and pagination
 * - HistoryTabData: Alert history data
 *
 * @see app/contexts/DashboardModalContext.tsx for similar pattern
 */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
  type RefObject,
} from 'react';
import type {
  EnhancedAlert,
  TrackedProduct,
  OnboardingData,
  AlertSeverity,
} from '../types/dashboard';
import type { AppliedFilter } from '../types/search';

// =============================================================================
// State Types - Grouped by Domain
// =============================================================================

/**
 * Tab navigation state
 */
export interface TabState {
  selectedTab: number;
  tabs: TabDescriptor[];
  isTabSwitching: boolean;
  onTabChange: (tabIndex: number) => void;
}

/**
 * Tab descriptor type (exported for consumers)
 */
export interface TabDescriptor {
  id: string;
  content: string;
  badge?: string;
  accessibilityLabel?: string;
}

/**
 * Search and autocomplete state
 */
export interface SearchState {
  isSearchExpanded: boolean;
  onSearchExpandChange: (expanded: boolean) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  autocompleteSuggestions: Array<{ label: string; value: string }>;
}

/**
 * Alert-specific filters (severity, days until stockout, inventory level)
 */
export interface AlertFiltersState {
  severityFilter: AlertSeverity[];
  onSeverityChange: (values: AlertSeverity[]) => void;
  daysMin: string;
  daysMax: string;
  onDaysMinChange: (value: string) => void;
  onDaysMaxChange: (value: string) => void;
  daysRangeError: boolean;
  inventoryMin: string;
  inventoryMax: string;
  onInventoryMinChange: (value: string) => void;
  onInventoryMaxChange: (value: string) => void;
  inventoryRangeError: boolean;
}

/**
 * Product-specific filters (status, type, vendor)
 */
export interface ProductFiltersState {
  statusFilter: string[];
  onStatusChange: (values: string[]) => void;
  productTypeFilter: string[];
  onProductTypeChange: (values: string[]) => void;
  productVendorFilter: string[];
  onProductVendorChange: (values: string[]) => void;
  productTypeOptions: string[];
  productVendorOptions: string[];
}

/**
 * Filter actions (clear all, remove pills)
 */
export interface FilterActionsState {
  onClearAllFilters: () => void;
  totalFilterCount: number;
  appliedFilterPills: Array<{ key: string; label: string }>;
  onRemoveFilterPill: (key: string) => void;
}

/**
 * Actions menu state (refresh, export, wizard)
 */
export interface ActionsMenuState {
  isActionsMenuOpen: boolean;
  onActionsMenuToggle: () => void;
  onActionsMenuClose: () => void;
  isRefreshing: boolean;
  onRefresh: () => void;
  onExport: () => void;
  isExporting: boolean;
  onOpenWizard: () => void;
}

/**
 * Active Alerts tab data and actions
 */
export interface AlertsTabDataState {
  filteredAlerts: EnhancedAlert[];
  activeAlertsCount: number;
  appliedAlertFilters: AppliedFilter[];
  dismissingAlerts: Set<number>;
  resolvingAlerts: Set<number>;
  notifyingAlerts: Set<number>;
  onResolve: (alert: EnhancedAlert) => void;
  onDismiss: (alert: EnhancedAlert) => void;
  onNotify: (alert: EnhancedAlert) => void;
  onAlertClick: (alert: EnhancedAlert) => void;
  hasNotificationChannels: boolean;
  onAlertFiltersClearAll: () => void;
  onPopulateTestData: () => void;
  isGeneratingTestData: boolean;
}

/**
 * Monitored Products tab data and pagination
 */
export interface ProductsTabDataState {
  filteredProducts: TrackedProduct[];
  onboardingData: OnboardingData | null;
  shopDomain: string;
  onProductClick: (product: TrackedProduct) => void;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

/**
 * Alert History tab data
 */
export interface HistoryTabDataState {
  filteredHistory: EnhancedAlert[];
}

/**
 * Shared state across tabs
 */
export interface SharedState {
  scrollRef: RefObject<HTMLDivElement | null>;
  hasTestData?: boolean;
}

// =============================================================================
// Context Value Type
// =============================================================================

/**
 * Complete MonitoringCenter context value
 */
export interface MonitoringCenterContextValue {
  // Grouped state
  shared: SharedState;
  tabs: TabState;
  search: SearchState;
  alertFilters: AlertFiltersState;
  productFilters: ProductFiltersState;
  filterActions: FilterActionsState;
  actionsMenu: ActionsMenuState;
  alertsTab: AlertsTabDataState;
  productsTab: ProductsTabDataState;
  historyTab: HistoryTabDataState;
}

// =============================================================================
// Context Creation
// =============================================================================

const MonitoringCenterContext = createContext<MonitoringCenterContextValue | null>(null);

/**
 * Hook to access full MonitoringCenter context
 * @throws Error if used outside MonitoringCenterProvider
 */
export function useMonitoringCenterContext(): MonitoringCenterContextValue {
  const context = useContext(MonitoringCenterContext);
  if (!context) {
    throw new Error('useMonitoringCenterContext must be used within MonitoringCenterProvider');
  }
  return context;
}

// =============================================================================
// Individual Domain Hooks
// =============================================================================

/**
 * Hook for shared state (ref, test data flag)
 */
export function useMonitoringShared(): SharedState {
  const ctx = useMonitoringCenterContext();
  return ctx.shared;
}

/**
 * Hook for tab state
 */
export function useMonitoringTabs(): TabState {
  const ctx = useMonitoringCenterContext();
  return ctx.tabs;
}

/**
 * Hook for search state
 */
export function useMonitoringSearch(): SearchState {
  const ctx = useMonitoringCenterContext();
  return ctx.search;
}

/**
 * Hook for alert filters
 */
export function useMonitoringAlertFilters(): AlertFiltersState {
  const ctx = useMonitoringCenterContext();
  return ctx.alertFilters;
}

/**
 * Hook for product filters
 */
export function useMonitoringProductFilters(): ProductFiltersState {
  const ctx = useMonitoringCenterContext();
  return ctx.productFilters;
}

/**
 * Hook for filter actions
 */
export function useMonitoringFilterActions(): FilterActionsState {
  const ctx = useMonitoringCenterContext();
  return ctx.filterActions;
}

/**
 * Hook for actions menu
 */
export function useMonitoringActionsMenu(): ActionsMenuState {
  const ctx = useMonitoringCenterContext();
  return ctx.actionsMenu;
}

/**
 * Hook for alerts tab data
 */
export function useMonitoringAlertsTab(): AlertsTabDataState {
  const ctx = useMonitoringCenterContext();
  return ctx.alertsTab;
}

/**
 * Hook for products tab data
 */
export function useMonitoringProductsTab(): ProductsTabDataState {
  const ctx = useMonitoringCenterContext();
  return ctx.productsTab;
}

/**
 * Hook for history tab data
 */
export function useMonitoringHistoryTab(): HistoryTabDataState {
  const ctx = useMonitoringCenterContext();
  return ctx.historyTab;
}

// =============================================================================
// Provider Props
// =============================================================================

/**
 * Props for MonitoringCenterProvider
 * Accepts all data and callbacks from parent, groups them internally
 */
export interface MonitoringCenterProviderProps {
  children: ReactNode;

  // Shared
  scrollRef: RefObject<HTMLDivElement | null>;
  hasTestData?: boolean;

  // Tab state
  selectedTab: number;
  tabs: TabDescriptor[];
  isTabSwitching: boolean;
  onTabChange: (tabIndex: number) => void;

  // Search state
  isSearchExpanded: boolean;
  onSearchExpandChange: (expanded: boolean) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  autocompleteSuggestions: Array<{ label: string; value: string }>;

  // Alert filters
  severityFilter: AlertSeverity[];
  onSeverityChange: (values: AlertSeverity[]) => void;
  daysMin: string;
  daysMax: string;
  onDaysMinChange: (value: string) => void;
  onDaysMaxChange: (value: string) => void;
  daysRangeError: boolean;
  inventoryMin: string;
  inventoryMax: string;
  onInventoryMinChange: (value: string) => void;
  onInventoryMaxChange: (value: string) => void;
  inventoryRangeError: boolean;

  // Product filters
  statusFilter: string[];
  onStatusChange: (values: string[]) => void;
  productTypeFilter: string[];
  onProductTypeChange: (values: string[]) => void;
  productVendorFilter: string[];
  onProductVendorChange: (values: string[]) => void;
  productTypeOptions: string[];
  productVendorOptions: string[];

  // Filter actions
  onClearAllFilters: () => void;
  totalFilterCount: number;
  appliedFilterPills: Array<{ key: string; label: string }>;
  onRemoveFilterPill: (key: string) => void;

  // Actions menu
  isActionsMenuOpen: boolean;
  onActionsMenuToggle: () => void;
  onActionsMenuClose: () => void;
  isRefreshing: boolean;
  onRefresh: () => void;
  onExport: () => void;
  isExporting: boolean;
  onOpenWizard: () => void;

  // Active Alerts tab data
  filteredAlerts: EnhancedAlert[];
  activeAlertsCount: number;
  appliedAlertFilters: AppliedFilter[];
  dismissingAlerts: Set<number>;
  resolvingAlerts: Set<number>;
  notifyingAlerts: Set<number>;
  onResolve: (alert: EnhancedAlert) => void;
  onDismiss: (alert: EnhancedAlert) => void;
  onNotify: (alert: EnhancedAlert) => void;
  onAlertClick: (alert: EnhancedAlert) => void;
  hasNotificationChannels: boolean;
  onAlertFiltersClearAll: () => void;
  onPopulateTestData: () => void;
  isGeneratingTestData: boolean;

  // Monitored Products tab data
  filteredProducts: TrackedProduct[];
  onboardingData: OnboardingData | null;
  shopDomain: string;
  onProductClick: (product: TrackedProduct) => void;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;

  // Alert History tab data
  filteredHistory: EnhancedAlert[];
}

// =============================================================================
// Provider Component
// =============================================================================

/**
 * Provider component for MonitoringCenter context
 * Groups all props into logical domains for consumption via hooks
 */
export function MonitoringCenterProvider({
  children,
  // Shared
  scrollRef,
  hasTestData,
  // Tab state
  selectedTab,
  tabs,
  isTabSwitching,
  onTabChange,
  // Search state
  isSearchExpanded,
  onSearchExpandChange,
  searchQuery,
  onSearchChange,
  autocompleteSuggestions,
  // Alert filters
  severityFilter,
  onSeverityChange,
  daysMin,
  daysMax,
  onDaysMinChange,
  onDaysMaxChange,
  daysRangeError,
  inventoryMin,
  inventoryMax,
  onInventoryMinChange,
  onInventoryMaxChange,
  inventoryRangeError,
  // Product filters
  statusFilter,
  onStatusChange,
  productTypeFilter,
  onProductTypeChange,
  productVendorFilter,
  onProductVendorChange,
  productTypeOptions,
  productVendorOptions,
  // Filter actions
  onClearAllFilters,
  totalFilterCount,
  appliedFilterPills,
  onRemoveFilterPill,
  // Actions menu
  isActionsMenuOpen,
  onActionsMenuToggle,
  onActionsMenuClose,
  isRefreshing,
  onRefresh,
  onExport,
  isExporting,
  onOpenWizard,
  // Active Alerts tab data
  filteredAlerts,
  activeAlertsCount,
  appliedAlertFilters,
  dismissingAlerts,
  resolvingAlerts,
  notifyingAlerts,
  onResolve,
  onDismiss,
  onNotify,
  onAlertClick,
  hasNotificationChannels,
  onAlertFiltersClearAll,
  onPopulateTestData,
  isGeneratingTestData,
  // Monitored Products tab data
  filteredProducts,
  onboardingData,
  shopDomain,
  onProductClick,
  currentPage,
  totalPages,
  itemsPerPage,
  onPageChange,
  // Alert History tab data
  filteredHistory,
}: MonitoringCenterProviderProps) {
  // Group state into domains
  const value = useMemo<MonitoringCenterContextValue>(() => ({
    shared: {
      scrollRef,
      hasTestData,
    },
    tabs: {
      selectedTab,
      tabs,
      isTabSwitching,
      onTabChange,
    },
    search: {
      isSearchExpanded,
      onSearchExpandChange,
      searchQuery,
      onSearchChange,
      autocompleteSuggestions,
    },
    alertFilters: {
      severityFilter,
      onSeverityChange,
      daysMin,
      daysMax,
      onDaysMinChange,
      onDaysMaxChange,
      daysRangeError,
      inventoryMin,
      inventoryMax,
      onInventoryMinChange,
      onInventoryMaxChange,
      inventoryRangeError,
    },
    productFilters: {
      statusFilter,
      onStatusChange,
      productTypeFilter,
      onProductTypeChange,
      productVendorFilter,
      onProductVendorChange,
      productTypeOptions,
      productVendorOptions,
    },
    filterActions: {
      onClearAllFilters,
      totalFilterCount,
      appliedFilterPills,
      onRemoveFilterPill,
    },
    actionsMenu: {
      isActionsMenuOpen,
      onActionsMenuToggle,
      onActionsMenuClose,
      isRefreshing,
      onRefresh,
      onExport,
      isExporting,
      onOpenWizard,
    },
    alertsTab: {
      filteredAlerts,
      activeAlertsCount,
      appliedAlertFilters,
      dismissingAlerts,
      resolvingAlerts,
      notifyingAlerts,
      onResolve,
      onDismiss,
      onNotify,
      onAlertClick,
      hasNotificationChannels,
      onAlertFiltersClearAll,
      onPopulateTestData,
      isGeneratingTestData,
    },
    productsTab: {
      filteredProducts,
      onboardingData,
      shopDomain,
      onProductClick,
      currentPage,
      totalPages,
      itemsPerPage,
      onPageChange,
    },
    historyTab: {
      filteredHistory,
    },
  }), [
    // Shared
    scrollRef,
    hasTestData,
    // Tabs
    selectedTab,
    tabs,
    isTabSwitching,
    onTabChange,
    // Search
    isSearchExpanded,
    onSearchExpandChange,
    searchQuery,
    onSearchChange,
    autocompleteSuggestions,
    // Alert filters
    severityFilter,
    onSeverityChange,
    daysMin,
    daysMax,
    onDaysMinChange,
    onDaysMaxChange,
    daysRangeError,
    inventoryMin,
    inventoryMax,
    onInventoryMinChange,
    onInventoryMaxChange,
    inventoryRangeError,
    // Product filters
    statusFilter,
    onStatusChange,
    productTypeFilter,
    onProductTypeChange,
    productVendorFilter,
    onProductVendorChange,
    productTypeOptions,
    productVendorOptions,
    // Filter actions
    onClearAllFilters,
    totalFilterCount,
    appliedFilterPills,
    onRemoveFilterPill,
    // Actions menu
    isActionsMenuOpen,
    onActionsMenuToggle,
    onActionsMenuClose,
    isRefreshing,
    onRefresh,
    onExport,
    isExporting,
    onOpenWizard,
    // Alerts tab
    filteredAlerts,
    activeAlertsCount,
    appliedAlertFilters,
    dismissingAlerts,
    resolvingAlerts,
    notifyingAlerts,
    onResolve,
    onDismiss,
    onNotify,
    onAlertClick,
    hasNotificationChannels,
    onAlertFiltersClearAll,
    onPopulateTestData,
    isGeneratingTestData,
    // Products tab
    filteredProducts,
    onboardingData,
    shopDomain,
    onProductClick,
    currentPage,
    totalPages,
    itemsPerPage,
    onPageChange,
    // History tab
    filteredHistory,
  ]);

  return (
    <MonitoringCenterContext.Provider value={value}>
      {children}
    </MonitoringCenterContext.Provider>
  );
}
